import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Collapse from 'react-bootstrap/Collapse';
import { toCamelCase } from 'utilities/utilities';
import { CHECKBOX_STATES, FIELD_TSIZE } from 'utilities/constants';
import './Checkbox.scss';

// 3 state checkbox with PPM styling
// need to implement this as an unmanaged component to support indeterminate state
export default function Checkbox({ inputOptions, childInputs, onChange }) {

    // destructure props and assign defaults
    const { label = "Input label", collapse = true, value = CHECKBOX_STATES.Unchecked, guid, field, disabled = false, controlSize = FIELD_TSIZE.Small } = inputOptions ?? {};

    // Expect state to be controlled when onChange event defined.
    // Otherwise "local" available when uncontrolled.
    const isControlled = (typeof onChange === 'function');
    const [localValue, setLocalValue] = React.useState(value);
    const [localChildValues, setLocalChildValues] = React.useState(childInputs);

    const uniqueCamelCaseLabel = `${toCamelCase(label)}${guid}`;
    

    // parent checkbox state and handlers 
    const [open, setOpen] = useState(!collapse);    // uncontrolled collapse state defaults to prop value
    const checkboxRef = React.useRef();

    let isChecked = (isControlled) ? value : localValue;
    let childChecks = (isControlled) ? childInputs : localChildValues;

    // update DOM checkbox manually when parent state changes to support indeterminate
    React.useEffect(() => {
        if (isChecked === CHECKBOX_STATES.Checked) {
            checkboxRef.current.checked = true;
            checkboxRef.current.indeterminate = false;
        } else if (isChecked === CHECKBOX_STATES.Unchecked) {
            checkboxRef.current.checked = false;
            checkboxRef.current.indeterminate = false;
        } else if (isChecked === CHECKBOX_STATES.Indeterminate) {
            checkboxRef.current.checked = false;
            checkboxRef.current.indeterminate = true;
        }
    }, [isChecked]);

    // parent checkbox clicks
    const handleChange = (e) => {
        if (isControlled) {
            let newValue = value === CHECKBOX_STATES.Checked ? CHECKBOX_STATES.Unchecked : CHECKBOX_STATES.Checked;
            onChange(
                e,
                field,
                guid,
                null,
                newValue);
        } else {
            let newValue = localValue === CHECKBOX_STATES.Checked ? CHECKBOX_STATES.Unchecked : CHECKBOX_STATES.Checked;
            setLocalValue(newValue);

            if (localChildValues) {
                let newLocalChildValues = localChildValues.map(function (x) { x.value = newValue; return x; });
                setLocalChildValues(newLocalChildValues);
            }
        }
    };

    // child checkbox clicks
    const handleChildChange = (childGuid, childValue, e) => {
        if (isControlled) {
            let newValue = childValue === CHECKBOX_STATES.Checked ? CHECKBOX_STATES.Unchecked : CHECKBOX_STATES.Checked;
            onChange(
                e,
                field,
                childGuid,
                guid,
                newValue);
        } else {
            let newValue = localChildValues.find(x => x.guid === childGuid).value === CHECKBOX_STATES.Checked ? CHECKBOX_STATES.Unchecked : CHECKBOX_STATES.Checked;
            let newLocalChildValues = localChildValues.map(function (x) { if (x.guid === childGuid) { x.value = newValue; } return x; });
            setLocalChildValues(newLocalChildValues);

            let childCheckCount = newLocalChildValues.filter(x => x.value === CHECKBOX_STATES.Checked).length;
            if (childCheckCount === 0) {
                setLocalValue(CHECKBOX_STATES.Unchecked);
            } else if (childCheckCount === newLocalChildValues.length) {
                setLocalValue(CHECKBOX_STATES.Checked);
            } else {
                setLocalValue(CHECKBOX_STATES.Indeterminate);
            }
        }
    }

    return (
        <div>
            <div className="d-flex justify-content-start align-items-center">
                <div>
                    <input type="checkbox"
                        className={`ppm ${isChecked === CHECKBOX_STATES.Indeterminate ? 'ppm-ind' : ''} checkbox-${controlSize}`}
                        ref={checkboxRef}
                        id={uniqueCamelCaseLabel}
                        onChange={handleChange}
                        disabled={disabled}
                    />
                    <label htmlFor={uniqueCamelCaseLabel}>{label}</label>
                </div>
                {childChecks &&
                    <Button variant="link" className="ms-3 checkbox-button"
                        onClick={() => setOpen(!open)}
                        aria-controls={`${uniqueCamelCaseLabel}Children`}
                        aria-expanded={open}
                        disabled={disabled}
                    >
                        <i className={`far ${open ? 'fa-minus-square' : 'fa-plus-square'}`}></i>
                    </Button>
                }
            </div>

            {childChecks &&
                <Collapse in={open}>
                    <div className="ps-5" id={`${uniqueCamelCaseLabel}Children`} data-testid="checkbox-child-wrapper">
                        {childChecks.map((input, index) => (
                            <ChildCheckbox key={index}
                                {...input}
                                controlSize={controlSize}
                                parentGuid={guid}
                                handleChange={handleChildChange}
                                disabled={disabled}
                            />
                        ))}
                    </div>
                </Collapse>  
            }
        </div>
    );
}

// simpler child checkboxes only need to support 2 states, can be managed
function ChildCheckbox({ label = 'Child Input Label', value, guid, parentGuid, handleChange, disabled, controlSize = FIELD_TSIZE.Small }) {

    const uniqueCamelCaseLabel = `${toCamelCase(label)}${parentGuid}`;

    return (
        <div>
            <input type="checkbox" className={`ppm checkbox-${controlSize}`}
                id={uniqueCamelCaseLabel}
                checked={value === CHECKBOX_STATES.Checked}
                onChange={(e) => handleChange(guid, value, e)}
                disabled={disabled}
            />
            <label htmlFor={uniqueCamelCaseLabel}>{label}</label>
        </div>  
    );
}

/*
Begin general helper functions for Checkboxes
*/

// IsOfCategory support
export function yesNoCheckboxState(fieldState) {
    let ret = {
        'yes': false,
        'no': false
    }
    Object.values(fieldState).forEach((checkbox) => {
        if (checkbox.name === 'Yes' && checkbox.value === CHECKBOX_STATES.Checked)
            ret['yes'] = true;
        if (checkbox.name === 'No' && checkbox.value === CHECKBOX_STATES.Checked)
            ret['no'] = true;
    })

    return ret;
}

export function binaryCheckboxState(fieldState) {
    // should only be one option for these, see if it is checked
    return (Object.values(fieldState)[0].value === CHECKBOX_STATES.Checked) ? true : null;
}

// get arrays of checkbox guids with the given state(s)
// returns a flat guid list and a list with parentGuids included
export function guidsOfCheckboxsWithState(fieldState, checkboxStates) {
    let guids = new Set();  // use Set to prevent duplicate guids
    let guidsWithParent = [];   // [[guid, parentGuid(optional)], ...]
    // each parent check
    Object.keys(fieldState).forEach((guid) => {
        if (checkboxStates.includes(fieldState[guid].value)) {
            guids.add(guid);
            guidsWithParent.push([guid]);
        }
        // check each child, if any
        const childGuids = Object.keys(fieldState[guid].children ?? {});
        if (childGuids.length > 0) {
            childGuids.forEach((childGuid) => {
                if (checkboxStates.includes(fieldState[guid].children[childGuid].value)) {
                    const beforeSize = guids.size;
                    guids.add(childGuid);

                    // add only if not a duplicate
                    if (beforeSize !== guids.size) {
                        guidsWithParent.push([childGuid, guid]);
                    }
                }
            });
        }
    });

    return [[...guids], guidsWithParent];
}

/*
Begin helper functions for Parent/Child Checkboxes
*/

// reducer function to coerce parent/child list into state object
export function hierarchyDefToCheckboxStateReducer(accumulator, currentParent, duplicateChildren, defaultSelections) {

    let childrenGuids = [];
    if (currentParent.children && currentParent.children.length > 0) {
        childrenGuids = currentParent.children.map((item) => { return item.guid; });
    }

    // build state object for this parent
    accumulator[currentParent.guid] = {
        name: currentParent.name,
        value: (defaultSelections && Object.values(defaultSelections)?.find(val => val === currentParent.guid)) ?
            CHECKBOX_STATES.Checked :
            (defaultSelections && childrenGuids.length > 0 && (Object.values(defaultSelections)?.filter(defVal => childrenGuids.find(childVal => childVal === defVal)).length > 0)) ?
                CHECKBOX_STATES.Indeterminate :
                CHECKBOX_STATES.Unchecked,
        children: currentParent.children.reduce((childAccumulator, currentChild) => {
            // make entries for duplicate tracking
            duplicateChildren[currentChild.guid] ?
                duplicateChildren[currentChild.guid].push(currentParent.guid) :
                duplicateChildren[currentChild.guid] = [currentParent.guid];
            // build state object for this child
            childAccumulator[currentChild.guid] = {
                name: currentChild.name,
                value: (defaultSelections && Object.values(defaultSelections)?.find(val => val === currentChild.guid)) ? CHECKBOX_STATES.Checked : CHECKBOX_STATES.Unchecked,
                duplicateChildParentGuids: null
            }
            return childAccumulator;
        }, {})
    };
    return accumulator;
}


// add duplicate references to child checkboxes in state for fast upates
export function addDuplicateCheckboxReferences(stateObj, duplicateChildren) {
    Object.keys(duplicateChildren).filter(childGuid => duplicateChildren[childGuid]?.length > 1).forEach((childGuid) => {
        // update duplicate child under each parent
        duplicateChildren[childGuid].forEach((parentGuid) => {
            stateObj[parentGuid].children[childGuid].duplicateChildParentGuids =
                duplicateChildren[childGuid].filter(g => g !== parentGuid);
        });
    });
}

// derive parent checkbox state from state of children
function parentCheckboxStateReducer(accumulator, current) {
    if (accumulator === CHECKBOX_STATES.Checked && current === CHECKBOX_STATES.Unchecked)
        return CHECKBOX_STATES.Indeterminate;
    else if (accumulator === CHECKBOX_STATES.Unchecked && current === CHECKBOX_STATES.Checked)
        return CHECKBOX_STATES.Indeterminate;
    else
        return accumulator;
}

export function setChildCheckboxValueAndUpdateParent(state, field, guid, parentGuid, newValue) {
    // check for duplicates first
    const hasDuplicates = state[field][parentGuid].children[guid].duplicateChildParentGuids != null;
    let newState = { ...state };
    let parentsToUpdate = [parentGuid];
    if (hasDuplicates) {
        parentsToUpdate = parentsToUpdate.concat(state[field][parentGuid].children[guid].duplicateChildParentGuids);
    }

    parentsToUpdate.forEach((currentParentGuid) => {
        // update child
        newState = {
            ...newState,
            [field]: {
                ...newState[field],
                [currentParentGuid]: {
                    ...newState[field][currentParentGuid],
                    children: {
                        ...newState[field][currentParentGuid].children,
                        [guid]: {
                            ...newState[field][currentParentGuid].children[guid],
                            value: newValue
                        }
                    }
                }
            }
        }
        // update parent based on state of children
        const newParentValue = Object.values(newState[field][currentParentGuid].children).map(c => c.value).reduce(parentCheckboxStateReducer);
        newState = {
            ...newState,
            [field]: {
                ...newState[field],
                [currentParentGuid]: {
                    ...newState[field][currentParentGuid],
                    value: newParentValue
                }
            }
        }
    });

    return newState;
}


export function setParentAndAllChildCheckboxValues(state, field, parentGuid, newValue) {
    let newState = { ...state };
    const hasChildren = state[field][parentGuid].children && Object.keys(state[field][parentGuid].children).length > 0;
    if (hasChildren) {
        const newChildren = Object.keys(state[field][parentGuid].children).reduce((accumulator, currentChildGuid) => {
            accumulator[currentChildGuid] = {
                ...state[field][parentGuid].children[currentChildGuid],
                value: newValue
            }
            // check for duplicates, update if necessary
            if (state[field][parentGuid].children[currentChildGuid].duplicateChildParentGuids) {
                state[field][parentGuid].children[currentChildGuid].duplicateChildParentGuids.forEach((duplicateParentGuid) => {
                    newState = setChildCheckboxValueAndUpdateParent(newState, field, currentChildGuid, duplicateParentGuid, newValue);
                });
            }

            return accumulator;
        }, {});
        // set parent value and replace updated children
        newState = {
            ...newState,
            [field]: {
                ...newState[field],
                [parentGuid]: {
                    ...newState[field][parentGuid],
                    value: newValue,
                    children: newChildren
                }
            }
        };
        return newState;
    }
    else {
        return {
            ...state,
            [field]: {
                ...state[field],
                [parentGuid]: {
                    ...state[field][parentGuid],
                    value: newValue
                }
            }
        };
    }
}

export function setAllCheckboxValues(state, field, newValue) {
    let newState = { ...state };

    // Set parents
    Object.keys(newState[field]).forEach((currentParentGuid) => {
        newState[field][currentParentGuid].value = newValue;

        // Set children of parents
        if (newState[field][currentParentGuid].children &&
            Object.keys(newState[field][currentParentGuid].children).length > 0) {

            Object.keys(newState[field][currentParentGuid].children).forEach((currentChildGuid) => {
                newState[field][currentParentGuid].children[currentChildGuid].value = newValue;
            });
        }
    });

    return newState;
}
